import React, { Suspense, useEffect } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import { CheckoutModalContext } from './context';
import useTopValue from 'hooks/useTopValue';
import useTokenContext from 'hooks/useTokenContext';
import useWalletContext from 'hooks/useWalletContext';
import { lightTheme, darkTheme } from 'styles/theme';
import { useDarkMode } from 'utils/useDarkMode';

import { routes } from './routes';
import { Header, LoadingBar, Notification } from 'components/General';
import { CustomSwitch, CustomRoute } from 'components/RouteComponents';
import CheckoutModal from 'components/General/CheckoutModal';
import BookmarkWarning from 'components/General/Warnings/BookmarkWarning';
import WrongNetworkWarning from 'components/General/Warnings/WrongNetworkWarning';
import MetamaskNotInstalledModal from 'components/MetamaskNotInstalledModal';

function RootContent() {
  const [theme, toggleTheme, componentMounted] = useDarkMode();
  const themeMode = theme === 'dark' ? darkTheme : lightTheme;
  const { marginTop } = useTopValue();
  const { signOut } = useTokenContext();
  const { isInstalled } = useWalletContext();
  const location = useLocation();

  const [checkoutModalOpen, setCheckoutModalOpen] = useStateIfMounted(false);
  const [modalData, setModalData] = useStateIfMounted({ metadata: {} });
  const [modalType, setModalType] = useStateIfMounted('place_bid');
  const [typeClicked, setTypeClicked] = useStateIfMounted('buy');

  // Removed automatic wallet verification on connection
  // Users will only be prompted when performing transactions

  useEffect(() => {
    if (!isInstalled) {
      signOut();
    }
  }, [isInstalled]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, [location.pathname]);

  if (!componentMounted) {
    return <div />;
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={themeMode}>
          <Notification />
          <MetamaskNotInstalledModal />
          <CheckoutModalContext.Provider
            value={{
              open: checkoutModalOpen,
              openModal: (data, modal, type) => {
                setModalData(data);
                setModalType(modal);
                setCheckoutModalOpen(true);
                setTypeClicked(type);
              },
              closeModal: () => {
                setCheckoutModalOpen(false);
                setModalType('');
                setTypeClicked('');
              },
            }}
          >
            <WrongNetworkWarning />
            <BookmarkWarning />
            <Header toggleTheme={toggleTheme} />
            <div className="root" style={{ marginTop }}>
              <Suspense fallback={<LoadingBar />}>
                <CustomSwitch>
                  {routes.map((item, index) => (
                    <CustomRoute key={index} {...item} />
                  ))}
                </CustomSwitch>
              </Suspense>
            </div>
            <CheckoutModal
              open={checkoutModalOpen}
              closeModal={() => {
                setCheckoutModalOpen(false);
                setModalType('');
                setTypeClicked('');
              }}
              data={modalData}
              type={modalType}
              path={typeClicked}
            />
          </CheckoutModalContext.Provider>
          <CssBaseline />
        </ThemeProvider>
      </LocalizationProvider>
    </>
  );
}

function Root() {
  return (
    <Router>
      <RootContent />
    </Router>
  );
}

export default Root;
